import { Container, ContainerGutter,TokenTextAppearance, TextTag, styled, CTA, Text } from '@volkswagen-onehub/components-core';
import { Contact, Pin } from '@volkswagen-onehub/icons-core';
import React from 'react';
import { useVehicleStore } from '../store/vehicles';
import { CustomDetailsCTA } from '../styles/components';
import { getLang, getTranslations } from '../translations';
import { VehicleData } from '../types';
import { DealerPropInterface } from '../types/dealers';
import { useFeatureServices } from '../use-feature-services';
import { formatDistance } from '../utils/format';
import { createConfiguration, createConstantData, createPartner, createProcess } from '../utils/get-tracking-event-data';
import useDealerUrl from '../utils/use-dealer-url';

const CustomGridLayout = styled.div`
    display: grid;
    grid-template-columns: 20px 1fr;

    > div {
        display: flex;
    }
`;

const LocalDealer = ({ dealer, showAction = false }: React.PropsWithoutRef<DealerPropInterface>) => {
    const { tracking } = useFeatureServices();
    const { labels } = getTranslations();
    const vehicle = useVehicleStore().vehicle as VehicleData;
    const lang = getLang();

    const dealerUrl = useDealerUrl(dealer);

    const onDealerWebsiteHandler = (e: React.SyntheticEvent<HTMLButtonElement>) => {
        window.open(dealer.website, '_blank')?.focus();
        tracking.track('VWBasic_DealerButton_Click', {
          ...createConstantData(),
          partner: createPartner(dealer),
          link: {
            url: dealer.website,
            name: e.currentTarget.innerText
          }
        });
    }

    const onDirectionHandler = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();

        const url = e.currentTarget.href;

        tracking.track('VWBasic_Button_Click', {
          ...createConstantData(),
          configuration: createConfiguration('VWBasic_Button_Click', vehicle, lang),
          partner: createPartner(dealer),
          process: createProcess("3"),
          link: {
            url,
            name: e.currentTarget.innerText
          }
        });

        window.open(url, '_blank');
    }

    return (
        <Container wrap="always" gutter={ContainerGutter.static200} stretchContent>
            <Container wrap="always" gutter={ContainerGutter.static0}>
                <Text appearance={TokenTextAppearance.headline200} bold>{dealer?.name}</Text>
                <Text tag={TextTag.span} appearance={TokenTextAppearance.copy100}>
                  { dealer && dealer.distance && labels.km_away.replace("%%distance%%", formatDistance(dealer?.distance, lang)) }
                </Text>
            </Container>

            <CustomGridLayout>
                <Container verticalAlign="center">
                    <Pin variant="small" ariaHidden />
                </Container>

                {dealerUrl && <CTA tag="a" emphasis='tertiary' href={dealerUrl} onClick={e => onDirectionHandler(e)}>
                  <Text appearance={TokenTextAppearance.copy200}>{dealer.address}, {dealer.city},&nbsp;{dealer.province},&nbsp;{dealer.postal}</Text>
                </CTA>}

                <Container verticalAlign="center">
                    <Contact variant="small" ariaLabel={labels.phone_number} />
                </Container>

                <CTA emphasis='tertiary' tag="a" href={'tel:+' + dealer?.phone}><Text appearance={TokenTextAppearance.copy200}>{dealer?.phone}</Text></CTA>
            </CustomGridLayout>

            {showAction && <Container
              wrap="auto"
              gutter={ContainerGutter.static200}
              horizontalAlign="flex-start"
            >

                <CustomDetailsCTA tag="button" emphasis="secondary" onClick={onDealerWebsiteHandler} ariaLabel={labels.dealer_website}>
                    { labels.dealer_website }
                </CustomDetailsCTA>
                {dealerUrl && <CustomDetailsCTA
                                    tag="a"
                                    emphasis="secondary"
                                    href={dealerUrl}
                                    ariaLabel={labels.directions}
                                    onClick={e => onDirectionHandler(e)}>
                    { labels.directions }
                </CustomDetailsCTA>}
            </Container> }
        </Container>
    );
}

export default LocalDealer;
